import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { dissoc, isNil, pathOr, propOr, values as ramdaValues } from 'ramda'
import { DateRangePicker } from 'react-dates'
import moment from 'moment'
import calendarIcon from 'assets/images/calendar.svg'
import AbsenceFormSelect from 'components/AbsenceFormSelect'
import { ABSENCE_TYPES } from 'utils/absences'
import Collapse from '@material-ui/core/Collapse'
import { Button, Input } from 'de-suite-me-ui'
import Checkbox from 'components/atoms/Checkbox'
import { addAbsenceRoutine } from 'features/calendar/ducks/actions'
import { useDispatch, useSelector } from 'react-redux'
import { formatDate, formatPayloadDate } from 'utils/date'
import { selectIsAddingAbsence } from 'ducks/loaderSelector'
import { selectIsAdmin } from 'features/auth/ducks/selectors'
import { selectHolidays } from 'features/calendar/ducks/selectors'
import { MobileContent, mobileCss, WebContent } from 'components/RWD'

const emptyValues = {
  dateFrom: moment(),
  dateTo: moment(),
  isHalfDay: false,
  comment: '',
  type: 'placeholder',
  status: 'accepted'
}

const AddAbsencePanel = ({ user, closeModal, profileView, viewType }) => {
  const [values, setValues] = useState(emptyValues)
  const [moreExpanded, setMoreExpanded] = useState(false)
  const [focusedInput, setFocusedInput] = useState(null)
  const dispatch = useDispatch()
  const isLoading = useSelector(selectIsAddingAbsence)
  const hasChildcareDays = user.annualChildcareDays > 0
  const isAdmin = useSelector(selectIsAdmin)
  const holidays = useSelector(selectHolidays)
  const holidaysDates = holidays.map(holiday => formatDate(holiday.date))

  const handleValueChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const handleCheckboxChange = (name, value) => {
    value && handleValueChange('dateTo', values.dateFrom)
    handleValueChange(name, value)
  }

  const onDateChange = dates => {
    switch (focusedInput) {
      case 'startDate':
        handleValueChange('dateFrom', dates.startDate)
        handleValueChange('dateTo', values.isHalfDay ? dates.startDate : dates.endDate)
        break
      case 'endDate':
        handleValueChange('dateFrom', values.isHalfDay ? dates.endDate : dates.startDate)
        handleValueChange('dateTo', dates.endDate)
        break
      default:
        return null
    }
  }

  const onFocusChange = inputType => {
    !isNil(focusedInput) && values.isHalfDay
      ? setFocusedInput(null)
      : setFocusedInput(inputType)
  }

  const handleMoreOptionOpen = () => setMoreExpanded(true)
  const handleMoreOptionClose = () => setMoreExpanded(false)

  const userName = propOr('', 'firstName', user)
  const daysLeft = propOr(0, 'availableHolidayDays', user)

  const handleSubmit = () => {
    dispatch(addAbsenceRoutine({
      ...values,
      employeeId: user.id,
      dateFrom: formatPayloadDate(values.dateFrom),
      dateTo: formatPayloadDate(values.dateTo),
      viewType,
      callback: () => {
        setValues(emptyValues)
        typeof closeModal === 'function' && closeModal()
      }
    }))
  }

  const isButtonDisabled = useMemo(() => {
    return ramdaValues(values).some(val => isNil(val)) || values.type === 'placeholder' || isLoading
  }, [values])

  const screenWidth = pathOr(0, ['screen', 'width'], window)

  return (
    <>
      <AddAbsencePanelTitle>
        <UserName>
          {userName},&nbsp;
        </UserName>
        <DaysLeftMessage>
          <WebContent>
            You have <DaysLeftAmount>{daysLeft}</DaysLeftAmount> holiday days until the end of the year️
          </WebContent>
          <MobileContent>
            You have <DaysLeftAmount>{daysLeft}</DaysLeftAmount> holiday days left️
          </MobileContent>
        </DaysLeftMessage>
      </AddAbsencePanelTitle>
      <AddAbsenceForm>
        <img src={calendarIcon} alt='calendar-icon' />
        <DateRangePicker
          appendToBody={profileView}
          small
          noBorder
          readOnly
          startDate={values.dateFrom}
          orientation={screenWidth < 768 ? 'vertical' : 'horizontal'}
          withFullScreenPortal={screenWidth < 768}
          startDateId={Math.random() * 99999}
          endDate={values.dateTo}
          endDateId={Math.random() * 99999}
          onDatesChange={onDateChange}
          focusedInput={focusedInput}
          onFocusChange={onFocusChange}
          isDayBlocked={day => [6, 7].includes(day.isoWeekday())}
          minimumNights={0}
          minDate={isAdmin ? moment().subtract(1, 'year') : moment()}
          displayFormat='DD.MM.YYYY'
          hideKeyboardShortcutsPanel
          isOutsideRange={day => {
            return propOr([], 'absences', user).some(absence =>
              (day.isBetween(absence.dateFrom, absence.dateTo, 'day', '[]')) ||
              holidaysDates.includes(formatDate(day))) && ![6, 7].includes(day.isoWeekday())
          }}
        />
        <FormDivider>/</FormDivider>
        <AbsenceFormSelect
          value={values.type}
          name='type'
          onChange={handleValueChange}
          options={
            hasChildcareDays
              ? ramdaValues(ABSENCE_TYPES)
              : ramdaValues(dissoc('childcare', ABSENCE_TYPES))
          }
        />
      </AddAbsenceForm>
      <MoreOptionsButton
        onClick={moreExpanded ? handleMoreOptionClose : handleMoreOptionOpen}
      >
        {moreExpanded ? 'Hide options' : 'More options'}
      </MoreOptionsButton>
      <Collapse in={moreExpanded}>
        <CheckboxWrapper>
          <Checkbox
            value={values.isHalfDay}
            name='isHalfDay'
            label='Is a half day absence'
            onChange={handleCheckboxChange}
          />
        </CheckboxWrapper>
        <Input
          name='comment'
          value={values.comment}
          onChange={e => handleValueChange('comment', e.target.value)}
          label='Comment'
        />
      </Collapse>
      <ButtonWrapper>
        <Button
          onClick={handleSubmit}
          color='secondary'
          disabled={isButtonDisabled}
        >
          Add absence
        </Button>
      </ButtonWrapper>
    </>
  )
}

export default AddAbsencePanel

const AddAbsencePanelTitle = styled.div`
  display: flex;
  
  & > div {
    display: inline-block;
  }
`

const UserName = styled.span`
  font-size: 19px;
  
  ${mobileCss(`
    display: none;
  `)}
`

const DaysLeftMessage = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray};
  padding-left: 5px;
  
  ${mobileCss(`
    margin-bottom: 5px;
  `)}
`

const DaysLeftAmount = styled.span`
  font-size: 18px;
  padding: 0 3px;
  color: ${({ theme }) => theme.palette.primary.main};
`

const AddAbsenceForm = styled.div`
  width: 100%;
  margin-top: 10px;
  border: 1px solid ${({ theme }) => theme.palette.common.whisper};
  border-radius: 4px;
  padding: 20px;
  display: flex;
  align-items: center;
  
  img {
    width: 30px;
    margin: 0 10px 2px 0;
    
    ${mobileCss(`
      margin-bottom: 10px;
    `)}
  }
  
  ${mobileCss(`
    flex-direction: column;
  `)}
`

const FormDivider = styled.div`
  color: ${({ theme }) => theme.palette.common.dustyGray};
  
  ${mobileCss(`
    display: none;
  `)}
`

const MoreOptionsButton = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.primary.main};
  cursor: pointer;
  transition: all .3s;
  margin: 7px 0;
  
  &:hover {
    color: ${({ theme }) => theme.palette.primary.dark};  
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`

const CheckboxWrapper = styled.div`
  margin: 10px 0;
`
